import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Select } from "./ui/Select";
import { Button, ButtonVariant, ButtonSize } from "@/shared/ui/Button";

interface PaginationProps {
  "data-testid": string;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalRecords: number;
  rowsPerPage: number;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  rowsPerPageOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  "data-testid": dataTestId,
  currentPage,
  totalPages,
  onPageChange,
  totalRecords,
  rowsPerPage,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50],
}) => {
  const { t } = useTranslation();

  // Detect SM viewport (< 768 px = Tailwind `md` breakpoint) so the rows-per-page
  // Select can switch between native <select> (MD+, no clipping concern) and
  // CustomSelect (SM, portal-rendered to escape the overflow-x-auto ancestor).
  // Follows the same matchMedia pattern as useIsLargeScreen.ts.
  // Reviewer HB1: useNative={!isSmViewport} — native at MD+, CustomSelect at SM.
  const [isSmViewport, setIsSmViewport] = useState<boolean>(
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? !window.matchMedia("(min-width: 768px)").matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsSmViewport(!e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Imperative live-region update via ref — React's vDOM diffing can sometimes
  // suppress the DOM mutation event that NVDA/JAWS rely on for aria-live announcement.
  // Writing textContent directly on the underlying DOM node guarantees the mutation fires.
  const liveRegionRef = useRef<HTMLSpanElement>(null);

  // Initialize to currentPage so the first useEffect run on mount is a no-op —
  // we only want to announce page TRANSITIONS, not the initial page state on load.
  const prevPageRef = useRef<number>(currentPage);

  useEffect(() => {
    if (prevPageRef.current === currentPage || !liveRegionRef.current) return;

    prevPageRef.current = currentPage;
    const msg = t("pagination.pageOf", { pageNumber: currentPage, totalPages });

    // Double-RAF clear-then-set pattern: canonical codebase approach (see RunScheduleTable,
    // SchedulesReferencesDropdown, RunStatusReferenceDropdown precedents).
    // First RAF clears the region; second RAF sets the new text — guarantees two separate
    // DOM mutations so NVDA/JAWS reliably fires the live-region announcement event.
    liveRegionRef.current.textContent = "";
    const raf1 = requestAnimationFrame(() => {
      if (!liveRegionRef.current) return;
      const raf2 = requestAnimationFrame(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = msg;
        }
      });
      // Cleanup inner RAF on unmount or re-run to prevent stale announcements
      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
  }, [currentPage, totalPages, t]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  const startRecord = (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, totalRecords);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    const half = Math.floor(maxPagesToShow / 2);

    if (totalPages <= maxPagesToShow + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else if (currentPage <= half + 1) {
      for (let i = 1; i <= maxPagesToShow; i++) {
        pageNumbers.push(i);
      }

      pageNumbers.push("...");
      pageNumbers.push(totalPages);
    } else if (currentPage >= totalPages - half) {
      pageNumbers.push(1);
      pageNumbers.push("...");
      for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      pageNumbers.push("...");
      for (let i = currentPage - half; i <= currentPage + half; i++) {
        pageNumbers.push(i);
      }

      pageNumbers.push("...");
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const pages = getPageNumbers();

  return (
    /*
     * Single-row layout — identical at all viewport widths including 600px.
     *
     * Reviewer HB2: overflow-x-auto is media-query-gated to SM only.
     *   • SM (< 768 px): overflow-x-auto — the bar scrolls horizontally when its
     *     content (min-w-max) is wider than the 600 px viewport. The inner div uses
     *     min-w-max to prevent line-wrapping. This is the only viewport size where
     *     the scroll container is needed.
     *   • MD+ (≥ 768 px): overflow-x-visible — NOT a scroll container. Restores the
     *     original CSS stacking context for all 14 consumers: sticky positioning,
     *     focus-visible outlines, and portal z-indices are unaffected at desktop widths.
     *
     * Reviewer HB1: Select adapts to viewport width — useNative={!isSmViewport}:
     *   • MD+ (≥ 768 px): useNative={true} → OS native <select>, full a11y, zero
     *     blast-radius on 14 consumers. No clipping concern (not inside overflow:auto).
     *   • SM (< 768 px): useNative={false} → CustomSelect with direction="up",
     *     portal-renders its dropdown to <body>, escaping the SM scroll container.
     */
    <div
      className="overflow-x-auto md:overflow-x-visible py-3 px-2"
      data-testid={dataTestId}
    >
      <div className="flex items-center justify-between gap-x-3 min-w-max md:min-w-0">

        {/* Left: page-nav buttons */}
        <div
          className="flex items-center gap-1"
          data-testid={`${dataTestId}-controls`}
        >
          <nav
            className="flex items-center gap-1"
            aria-label={t("pagination.label")}
            data-testid={`${dataTestId}-nav`}
          >
            {/* First page button */}
            <Button
              variant={ButtonVariant.Outline}
              size={ButtonSize.Xs}
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              data-testid={`${dataTestId}-first-button`}
              className="w-8 h-8 p-0"
              aria-label={t("pagination.firstPage")}
            >
              <ChevronsLeft className="w-4 h-4" aria-hidden="true" />
            </Button>
            {/* Previous page button */}
            <Button
              variant={ButtonVariant.Outline}
              size={ButtonSize.Xs}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              data-testid={`${dataTestId}-previous-button`}
              className="w-8 h-8 p-0"
              aria-label={t("pagination.previousPage")}
            >
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            </Button>
            {/* Page numbers */}
            {pages.map((page, index) =>
              typeof page === "string" ? (
                <span
                  key={`ellipsis-${index}`}
                  className="px-3 py-2 text-sm font-medium text-text"
                  data-testid={`${dataTestId}-ellipsis-${index}`}
                  aria-hidden="true"
                >
                  ...
                </span>
              ) : (
                <Button
                  key={page}
                  variant={currentPage === page ? ButtonVariant.Primary : ButtonVariant.Outline}
                  size={ButtonSize.Xs}
                  onClick={() => handlePageChange(page)}
                  data-testid={`${dataTestId}-page-${page}-button`}
                  className={`${page >= 1000 ? 'min-w-8 px-2' : 'w-8'} h-8 p-0`}
                  aria-label={t("pagination.page", { pageNumber: page })}
                  aria-current={currentPage === page ? "page" : undefined}
                >
                  {page}
                </Button>
              ),
            )}
            {/* Next page button */}
            <Button
              variant={ButtonVariant.Outline}
              size={ButtonSize.Xs}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              data-testid={`${dataTestId}-next-button`}
              className="w-8 h-8 p-0"
              aria-label={t("pagination.nextPage")}
            >
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </Button>
            {/* Last page button */}
            <Button
              variant={ButtonVariant.Outline}
              size={ButtonSize.Xs}
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              data-testid={`${dataTestId}-last-button`}
              className="w-8 h-8 p-0"
              aria-label={t("pagination.lastPage")}
            >
              <ChevronsRight className="w-4 h-4" aria-hidden="true" />
            </Button>
          </nav>
          {/* Imperative textContent update via ref — bypasses React vDOM diffing so
              NVDA/JAWS always fires the live-region mutation event (see useEffect above).
              role="status" implies aria-live="polite" and aria-atomic="true" per ARIA spec;
              the explicit attributes are retained as a defensive override because some
              older NVDA/JAWS versions did not honour the implicit values correctly. */}
          <span
            ref={liveRegionRef}
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
          />
        </div>

        {/* Right: showing info + rows-per-page select */}
        <div className="flex items-center gap-3">
          <p className="text-sm text-text whitespace-nowrap" data-testid={`${dataTestId}-info`}>
            {t("pagination.showing", {
              start: startRecord.toLocaleString("en-US"),
              end: endRecord.toLocaleString("en-US"),
              total: totalRecords.toLocaleString("en-US"),
            })}
          </p>
          {/* Spec Pass 2 item #53 — label sits BEFORE the dropdown
              (standard pattern: "Results per page: [10▾]" reads
              left-to-right naturally). Was previously dropdown-then-
              label which forced the eye to backtrack. */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-text whitespace-nowrap">{t("pagination.resultsPerPage")}</span>
            {/* Reviewer HB1: useNative={!isSmViewport}
                SM (< 768 px): useNative={false} → CustomSelect portal-renders its dropdown
                  to <body> with direction="up", escaping the overflow-x-auto scroll container
                  so the dropdown appears properly aligned above the trigger at 600 px.
                MD+ (≥ 768 px): useNative={true} → OS native <select>, full a11y
                  (OS-level keyboard/touch), zero blast-radius on 14 Pagination consumers. */}
            <Select
              value={rowsPerPage}
              onChange={onRowsPerPageChange}
              useNative={!isSmViewport}
              className="min-w-[80px]"
              label=""
              aria-label={t("pagination.rowsPerPage")}
              data-testid={`${dataTestId}-rows-per-page-select`}
              direction="up"
              options={rowsPerPageOptions.map((option) => ({
                value: option,
                label: String(option),
              }))}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default React.memo(Pagination);
